import { z } from 'zod';

import { METRIC_INDICATOR_STATES } from '@/components/common/metric-indicator';

import { DEVICE_STATUS } from './constants';
import { SUBSCRIPTION_INDICATOR } from './details-schemas';
import { MAP_DEVICE_STATUSES } from './map-schemas';

/**
 * One metric's pre-thresholded state. `null` for a metric with no reading — and, deliberately, for a
 * state this build does not recognise: `parseContract`'s Sentry signal reports the drift loudly while
 * the panel shows no indicator rather than a wrong one. Same conservative shape `locationSource` takes
 * in `map-schemas.ts`, and the reason is the same: one unknown enum value must not blank a whole
 * surface.
 */
const indicatorState = z.enum(METRIC_INDICATOR_STATES).nullable().catch(null);

/** A pre-formatted reading, unit included (`"2.7 V"`). `null` ⇒ the dash placeholder (§6d). */
const displayValue = z.string().nullable().catch(null);

/**
 * `GET /devices/tracking/{customerDeviceId}/map-popup` — identity, status, subscription badge, three
 * readings and last sync.
 *
 * **Nothing here is computed.** `display.*` arrives formatted and `indicators.*` arrives thresholded,
 * which is exactly what 113588 §17a demands of the panel: no precision, no unit, no rounding and no
 * threshold is the frontend's to decide. The raw numbers alongside them (`batteryVoltage`,
 * `temperatureF`, `waterLevelInches`) are deliberately NOT modelled — modelling them would invite a
 * second, divergent formatter.
 */
export const deviceMapPopupSchema = z.object({
  id: z.string(),
  customName: z.string().nullable().catch(null),
  deviceName: z.string(),
  /** Same six tokens the marker layer speaks, and the same fallback, so a status the panel and the
   *  marker disagree about is impossible. */
  effectiveStatus: z.enum(MAP_DEVICE_STATUSES).catch(DEVICE_STATUS.PENDING_CONFIGURATION),
  subscriptionIndicator: z
    .enum([
      SUBSCRIPTION_INDICATOR.PROTECTED,
      SUBSCRIPTION_INDICATOR.EXPIRING_SOON,
      SUBSCRIPTION_INDICATOR.EXPIRED,
      SUBSCRIPTION_INDICATOR.NONE,
    ])
    .catch(SUBSCRIPTION_INDICATOR.NONE),
  connectivity: z
    .object({ lastSyncedAt: z.string().nullable().catch(null) })
    .catch({ lastSyncedAt: null }),
  metrics: z.object({
    display: z.object({
      batteryVoltage: displayValue,
      temperature: displayValue,
      waterLevel: displayValue,
    }),
    indicators: z.object({
      batteryVoltage: indicatorState,
      temperature: indicatorState,
      waterLevel: indicatorState,
    }),
  }),
});

export type DeviceMapPopup = z.infer<typeof deviceMapPopupSchema>;

/**
 * A `telemetry` channel event for one Device.
 *
 * **This shape is an assumption, and a loud one** (grilling F11): the backend has published no
 * telemetry payload contract, so it is derived from `deviceMapPopupSchema` rather than invented —
 * every field is the one the panel already renders, made optional, with `id` still required so an
 * event can be routed to the Device it belongs to (§9g). Deriving rather than restating means the
 * two cannot drift, and means BE publishing something else is a one-file change here.
 *
 * **Absent ≠ null.** An absent key is "no change" and is preserved by `applyTelemetry`; an explicit
 * `null` is a real value — a metric with no reading, which renders the dash (§6d). That distinction
 * is what makes §9d's "freeze at the last received readings rather than clearing to blank" hold for
 * a partial event.
 *
 * A payload that does not parse at all writes nothing (see `useLiveTelemetry`), so an unrecognised
 * contract degrades to the frozen values rather than to a blanked panel.
 */
const partialMetrics = z.object({
  display: deviceMapPopupSchema.shape.metrics.shape.display.partial().optional(),
  indicators: deviceMapPopupSchema.shape.metrics.shape.indicators.partial().optional(),
});

export const telemetryUpdateSchema = deviceMapPopupSchema
  .omit({ metrics: true })
  .partial()
  .extend({ id: z.string(), metrics: partialMetrics.optional() });

export type TelemetryUpdate = z.infer<typeof telemetryUpdateSchema>;
